import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import {
  Building2,
  Check,
  Database,
  ExternalLink,
  Eye,
  Gauge,
  Globe2,
  Image as ImageIcon,
  Loader2,
  Lock,
  Mail,
  KeyRound,
  Mic,
  MessageSquare,
  Layers,
  Palette,
  Save,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import SlaSettingsTab from "@/components/SlaSettingsTab";
import AccessSettingsTab from "@/components/AccessSettingsTab";
import PipelinesSettingsTab from "@/components/PipelinesSettingsTab";
import WaCommandCentreTab from "@/components/WaCommandCentreTab";
import DataSettingsTab from "@/components/DataSettingsTab";

const FALLBACK_COUNTRIES = [
  "Russia",
  "Georgia",
  "Uzbekistan",
  "Kyrgyzstan",
  "Kazakhstan",
  "Bangladesh",
  "Philippines",
];

const PLAN_LABELS = {
  starter: { l: "Starter", c: "bg-slate-100 text-slate-700 border-slate-300" },
  growth: { l: "Growth", c: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  enterprise: { l: "Enterprise", c: "bg-amber-100 text-amber-800 border-amber-300" },
};

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [caps, setCaps] = useState({});
  const [branding, setBranding] = useState(null);
  const [dirty, setDirty] = useState({});
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings");
      setTenant(r.data.tenant);
      setBranding(r.data.branding);
      setCaps(r.data.capabilities);
      setDirty({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setField = (k, v) => {
    setBranding((b) => ({ ...b, [k]: v }));
    setDirty((d) => ({ ...d, [k]: v }));
  };

  const hasChanges = Object.keys(dirty).length > 0;

  const onSave = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/settings", dirty);
      setBranding(r.data.branding);
      setDirty({});
      toast.success("Branding saved ✓");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onLogoFile = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const data = String(reader.result || "");
      if (!data.startsWith("data:image/")) {
        toast.error("Pick an image file (PNG / JPG / SVG)");
        return;
      }
      setField("logo", data);
    };
    reader.readAsDataURL(file);
  };

  if (loading || !branding) {
    return (
      <div className="min-h-screen bg-[#F6F8FB] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#C9A961]" />
      </div>
    );
  }

  const plan = (tenant?.plan || "starter").toLowerCase();
  const planMeta = PLAN_LABELS[plan] || PLAN_LABELS.starter;
  const previewUrl = `/t/${tenant.slug}`;

  return (
    <div className="min-h-screen bg-[#F6F8FB]" data-testid="settings-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="mx-auto max-w-5xl px-5 pl-16 py-3 flex items-center gap-4 md:pl-5">
          <div className="mr-auto">
            <div className="font-display text-base font-semibold text-[#0A2540]">
              Tenant Settings
            </div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span>{tenant.name}</span> ·{" "}
              <Badge variant="outline" className={`${planMeta.c} text-[10px] uppercase`}>
                {planMeta.l} plan
              </Badge>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(previewUrl, "_blank")}
            data-testid="settings-preview-btn"
          >
            <Eye className="h-4 w-4 mr-1.5" /> Preview landing
          </Button>
          <Button
            size="sm"
            disabled={!hasChanges || saving}
            onClick={onSave}
            className="bg-[#0A2540] hover:bg-[#1E3A5F] disabled:opacity-50"
            data-testid="settings-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            {hasChanges ? "Save changes" : "Saved"}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8">
        <Tabs defaultValue="identity" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="identity" data-testid="tab-identity">
              <Palette className="h-4 w-4 mr-1.5" /> Identity
            </TabsTrigger>
            <TabsTrigger value="landing" data-testid="tab-landing">
              <Globe2 className="h-4 w-4 mr-1.5" /> Landing
            </TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
              <MessageSquare className="h-4 w-4 mr-1.5" /> WhatsApp
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <Mail className="h-4 w-4 mr-1.5" /> Notifications
            </TabsTrigger>
            <TabsTrigger value="sla" data-testid="tab-sla">
              <Gauge className="h-4 w-4 mr-1.5" /> SLA
            </TabsTrigger>
            <TabsTrigger value="access" data-testid="tab-access">
              <Lock className="h-4 w-4 mr-1.5" /> Access
            </TabsTrigger>
            <TabsTrigger value="pipelines" data-testid="tab-pipelines">
              <Layers className="h-4 w-4 mr-1.5" /> Pipelines
            </TabsTrigger>
            <TabsTrigger value="wa-centre" data-testid="tab-wa-centre">
              <MessageSquare className="h-4 w-4 mr-1.5" /> WA Command
            </TabsTrigger>
            <TabsTrigger value="data" data-testid="tab-data">
              <Database className="h-4 w-4 mr-1.5" /> Data
            </TabsTrigger>
            <TabsTrigger value="share" data-testid="tab-share">
              <ExternalLink className="h-4 w-4 mr-1.5" /> Share form
            </TabsTrigger>
          </TabsList>

          {/* --- IDENTITY --- */}
          <TabsContent value="identity">
            <Section
              title="Brand identity"
              hint="How your consultancy shows up to students."
            >
              {/* Logo */}
              <Row label="Logo" cap={caps.logo} plan={plan}>
                <div className="flex items-start gap-4">
                  <div className="h-24 w-24 rounded-lg border border-dashed border-slate-300 bg-white flex items-center justify-center overflow-hidden">
                    {branding.logo ? (
                      <img src={branding.logo} alt="Logo" className="max-h-20 max-w-20 object-contain" />
                    ) : (
                      <ImageIcon className="h-7 w-7 text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onLogoFile(e.target.files?.[0])}
                      data-testid="settings-logo-input"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!caps.logo}
                      data-testid="settings-logo-upload"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload PNG / JPG / SVG
                    </Button>
                    {branding.logo && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setField("logo", null)}
                        className="text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                      </Button>
                    )}
                    <div className="text-[11px] text-slate-500">
                      Up to 2 MB. Square logos work best (e.g. 512×512).
                    </div>
                  </div>
                </div>
              </Row>

              <Row label="Brand display name" cap={caps.brand_display_name} plan={plan} hint="Falls back to your tenant name.">
                <Input
                  value={branding.brand_display_name || ""}
                  onChange={(e) => setField("brand_display_name", e.target.value || null)}
                  placeholder={tenant.name}
                  disabled={!caps.brand_display_name}
                  data-testid="settings-display-name"
                />
              </Row>

              <Row label="Tagline" cap={caps.tagline} plan={plan}>
                <Input
                  value={branding.tagline || ""}
                  onChange={(e) => setField("tagline", e.target.value || null)}
                  maxLength={160}
                  placeholder="Your gateway to MBBS abroad."
                  disabled={!caps.tagline}
                  data-testid="settings-tagline"
                />
              </Row>

              <Row label="Primary color" cap={caps.primary_color} plan={plan} hint="Drives buttons & accents on your landing page.">
                <ColorField
                  value={branding.primary_color}
                  onChange={(v) => setField("primary_color", v)}
                  disabled={!caps.primary_color}
                  testid="settings-primary-color"
                />
              </Row>

              <Row label="Accent color" cap={caps.accent_color} plan={plan} hint="Used for highlights & badges.">
                <ColorField
                  value={branding.accent_color}
                  onChange={(v) => setField("accent_color", v)}
                  disabled={!caps.accent_color}
                  testid="settings-accent-color"
                />
              </Row>
            </Section>
            <div className="mt-4">
              <ChangePasswordPanel />
            </div>
          </TabsContent>

          {/* --- LANDING --- */}
          <TabsContent value="landing">
            <Section title="Landing page" hint={`Public URL: ${window.location.origin}/t/${tenant.slug}`}>
              <Row label="Hero headline" cap={caps.hero_headline} plan={plan} hint="Big bold line at the top.">
                <Input
                  value={branding.hero_headline || ""}
                  onChange={(e) => setField("hero_headline", e.target.value || null)}
                  placeholder="Score scholarships before NEET results"
                  disabled={!caps.hero_headline}
                  data-testid="settings-hero-headline"
                />
              </Row>
              <Row label="Hero sub-headline" cap={caps.hero_sub} plan={plan}>
                <Textarea
                  rows={2}
                  value={branding.hero_sub || ""}
                  onChange={(e) => setField("hero_sub", e.target.value || null)}
                  placeholder="Take the 10-minute scholarship test and unlock up to 50% off tuition."
                  disabled={!caps.hero_sub}
                  data-testid="settings-hero-sub"
                  className="resize-none"
                />
              </Row>
              <Row label="Country focus" cap={caps.country_focus} plan={plan} hint="Which countries should appear on your landing.">
                <CountryPicker
                  selected={branding.country_focus || []}
                  onChange={(arr) => setField("country_focus", arr)}
                  disabled={!caps.country_focus}
                />
              </Row>
            </Section>
          </TabsContent>

          {/* --- WHATSAPP --- */}
          <TabsContent value="whatsapp">
            <Section title="WhatsApp templates" hint="Used when your counsellors send scholarship reports & nudges.">
              <Row label="Greeting line" cap={caps.wa_greeting} plan={plan} hint="Use {name} for the student's first name.">
                <Input
                  value={branding.wa_greeting || ""}
                  onChange={(e) => setField("wa_greeting", e.target.value || null)}
                  placeholder="Hi {name}, this is Riya from Sunrise MBBS — your scholarship report is ready."
                  disabled={!caps.wa_greeting}
                  data-testid="settings-wa-greeting"
                />
              </Row>
              <Row label="Signature" cap={caps.wa_signature} plan={plan}>
                <Input
                  value={branding.wa_signature || ""}
                  onChange={(e) => setField("wa_signature", e.target.value || null)}
                  placeholder="— Team Sunrise MBBS"
                  disabled={!caps.wa_signature}
                  data-testid="settings-wa-signature"
                />
              </Row>
              <Row label="PDF caption" cap={caps.wa_pdf_caption} plan={plan} hint="Text that travels with the scholarship report PDF.">
                <Textarea
                  rows={2}
                  value={branding.wa_pdf_caption || ""}
                  onChange={(e) => setField("wa_pdf_caption", e.target.value || null)}
                  placeholder="Your detailed scholarship report is attached. Reply to this message with any questions."
                  disabled={!caps.wa_pdf_caption}
                  className="resize-none"
                />
              </Row>
            </Section>
          </TabsContent>

          {/* --- NOTIFICATIONS --- */}
          <TabsContent value="notifications">
            <Section title="Notifications & sender" hint="Where your tenant's alerts land + how outbound emails are signed.">
              <Row
                label="Email sender display name"
                cap={caps.email_sender_name}
                plan={plan}
                hint="Shown as the From-name in transactional emails (address stays MedAdmits)."
              >
                <Input
                  value={branding.email_sender_name || ""}
                  onChange={(e) => setField("email_sender_name", e.target.value || null)}
                  placeholder="Sunrise MBBS"
                  disabled={!caps.email_sender_name}
                  data-testid="settings-email-sender"
                />
              </Row>
              <Row label="Daily-digest recipients" cap={caps.notify_emails} plan={plan} hint="Comma-separated; up to 6 emails.">
                <EmailListField
                  value={branding.notify_emails || []}
                  onChange={(arr) => setField("notify_emails", arr)}
                  disabled={!caps.notify_emails}
                />
              </Row>
              <Row label="Slack webhook (per-tenant)" cap={caps.slack_webhook} plan={plan} hint="Fires when leads register / mark hot.">
                <Input
                  value={branding.slack_webhook || ""}
                  onChange={(e) => setField("slack_webhook", e.target.value || null)}
                  placeholder="https://hooks.slack.com/services/..."
                  disabled={!caps.slack_webhook}
                />
              </Row>
              <Row
                label="Daily digest"
                hint="Auto-sent every morning at 09:00 IST. Counsellors get their morning brief; admins get the State-of-Pipeline."
              >
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const r = await api.post("/admin/digests/preview?kind=admin");
                        const w = window.open("", "_blank");
                        if (w) {
                          w.document.write(r.data.html);
                          w.document.title = r.data.subject || "Digest preview";
                        }
                      } catch (e) {
                        toast.error("Preview failed");
                      }
                    }}
                    data-testid="settings-preview-admin-digest"
                  >
                    Preview admin digest
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const r = await api.post("/admin/digests/preview?kind=counsellor");
                        const w = window.open("", "_blank");
                        if (w) {
                          w.document.write(r.data.html);
                          w.document.title = r.data.subject || "Digest preview";
                        }
                      } catch (e) {
                        toast.error("Preview failed");
                      }
                    }}
                    data-testid="settings-preview-counsellor-digest"
                  >
                    Preview counsellor brief
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="bg-[#0A2540] hover:bg-[#1E3A5F]"
                    onClick={async () => {
                      try {
                        const r = await api.post("/admin/digests/send-now?kind=admin");
                        if (r.data?.sent) toast.success("Admin digest sent");
                        else toast.warning(r.data?.reason || "Nothing to send");
                      } catch (e) {
                        toast.error("Send failed");
                      }
                    }}
                    data-testid="settings-send-admin-digest"
                  >
                    Send admin digest now
                  </Button>
                </div>
              </Row>
            </Section>
            <div className="mt-4">
              <WhisperUsagePanel />
            </div>
          </TabsContent>

          {/* --- SHARE FORM --- */}
          <TabsContent value="sla">
            <Section
              title="Outreach SLA targets"
              hint="Set your tenant's Speed-to-Lead & follow-up SLAs per lead temperature. Admin only."
            >
              <div className="px-6 py-5">
                <SlaSettingsTab />
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="access">
            <Section
              title="Team access & lead visibility"
              hint="Control what your counsellors can see. Admin only."
            >
              <div className="px-6 py-5">
                <AccessSettingsTab />
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="pipelines">
            <Section
              title="Custom sections (pipelines)"
              hint="Create your own boards like Leads & Counselling. Name a section, then add, rename, recolour, reorder or remove its stages. Admin only."
            >
              <div className="px-6 py-5">
                <PipelinesSettingsTab />
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="wa-centre">
            <Section
              title="Click-to-WhatsApp Command Centre"
              hint="Design WhatsApp templates per pipeline & stage with personalization tokens. Counsellors pick them from the lead's WhatsApp button. Admin only."
            >
              <div className="px-6 py-5">
                <WaCommandCentreTab />
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="data">
            <Section
              title="Import & export leads"
              hint="Bulk-import leads from a CSV, or export your leads to a spreadsheet filtered by pipeline and stage."
            >
              <div className="px-6 py-5">
                <DataSettingsTab />
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="share">
            <Section
              title="Lead-capture form & QR code"
              description="Share this URL or print the QR code on flyers / WhatsApp / Instagram to collect leads instantly. Every submission is auto-attributed to your tenant."
            >
              <ShareFormPanel tenant={tenant} branding={branding} />
            </Section>
          </TabsContent>
        </Tabs>

        {/* Preview footer */}
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5 flex items-center gap-4">
          <Globe2 className="h-8 w-8 text-[#C9A961] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#0A2540]">
              Your branded landing is live at
            </div>
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-blue-600 hover:underline truncate inline-block max-w-full"
            >
              {window.location.origin}/t/{tenant.slug}
              <ExternalLink className="inline h-3 w-3 ml-1" />
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}

/* ---------------------- Sub-components ---------------------- */

function Section({ title, hint, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100">
        <h2 className="font-display font-semibold text-[#0A2540]">{title}</h2>
        {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function Row({ label, hint, children, cap, plan }) {
  return (
    <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-start">
      <div className="md:col-span-4">
        <Label className="text-sm font-medium text-[#0A2540] flex items-center gap-1.5">
          {label}
          {!cap && (
            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[9px] uppercase tracking-wider">
              <Lock className="h-2.5 w-2.5 mr-0.5" /> Growth+
            </Badge>
          )}
        </Label>
        {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
        {!cap && (
          <p className="mt-1.5 text-[11px] text-amber-700">
            Available on <strong>Growth</strong> & <strong>Enterprise</strong> plans.
          </p>
        )}
      </div>
      <div className="md:col-span-8">{children}</div>
    </div>
  );
}

function ColorField({ value, onChange, disabled, testid }) {
  const safe = value && /^#[0-9A-Fa-f]{6}$/.test(value) ? value : "#0A2540";
  return (
    <div className="flex items-center gap-3">
      <div
        className="h-10 w-10 rounded-md border border-slate-200 shrink-0"
        style={{ backgroundColor: safe }}
        data-testid={`${testid}-swatch`}
      />
      <input
        type="color"
        value={safe}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        disabled={disabled}
        className="h-10 w-12 rounded border border-slate-200 disabled:opacity-50 cursor-pointer"
        data-testid={`${testid}-picker`}
      />
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#0A2540"
        disabled={disabled}
        className="font-mono-clock max-w-[140px]"
        data-testid={testid}
      />
    </div>
  );
}

function CountryPicker({ selected, onChange, disabled }) {
  const toggle = (c) =>
    onChange(selected.includes(c) ? selected.filter((x) => x !== c) : [...selected, c]);
  return (
    <div className="flex flex-wrap gap-2">
      {FALLBACK_COUNTRIES.map((c) => {
        const active = selected.includes(c);
        return (
          <button
            key={c}
            type="button"
            disabled={disabled}
            onClick={() => toggle(c)}
            data-testid={`country-chip-${c.toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors disabled:opacity-50 ${
              active
                ? "bg-[#0A2540] border-[#0A2540] text-white"
                : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
            }`}
          >
            {active && <Check className="h-3 w-3 inline mr-1" />}
            {c}
          </button>
        );
      })}
    </div>
  );
}

function EmailListField({ value, onChange, disabled }) {
  const [text, setText] = useState(value.join(", "));
  useEffect(() => {
    setText(value.join(", "));
  }, [value.join(",")]);
  return (
    <Textarea
      rows={2}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() =>
        onChange(
          text
            .split(/[,\n]/)
            .map((s) => s.trim().toLowerCase())
            .filter((s) => s.includes("@"))
            .slice(0, 6)
        )
      }
      disabled={disabled}
      placeholder="amit@sunrise.in, riya@sunrise.in"
      className="resize-none"
      data-testid="settings-notify-emails"
    />
  );
}

function ShareFormPanel({ tenant }) {
  const qrRef = useRef(null);
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const formUrl = tenant?.slug
    ? `${baseUrl}/t/${tenant.slug}/lead-form`
    : `${baseUrl}/lead-form`;
  const landingUrl = tenant?.slug ? `${baseUrl}/t/${tenant.slug}` : `${baseUrl}/`;

  const [counsellors, setCounsellors] = useState([]);
  const [loadingCouns, setLoadingCouns] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/counsellors")
      .then((r) => {
        if (!cancelled) setCounsellors(r.data?.counsellors || []);
      })
      .catch(() => {
        if (!cancelled) setCounsellors([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCouns(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const copy = (text, label) => {
    if (typeof navigator === "undefined") return;
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error("Couldn't copy — please select & copy manually"));
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tenant?.slug || "lead-form"}-qr.png`;
    a.click();
  };

  const personalUrl = (userId) =>
    `${formUrl}${formUrl.includes("?") ? "&" : "?"}ref=${userId}`;

  return (
    <div className="space-y-8">
      <div className="grid gap-6 md:grid-cols-2">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
          Public form URL
        </div>
        <div className="mt-1.5 flex gap-2">
          <Input
            readOnly
            value={formUrl}
            onClick={(e) => e.target.select()}
            data-testid="share-form-url"
            className="font-mono text-xs"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => copy(formUrl, "Form URL")}
            data-testid="share-copy-form"
          >
            Copy
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Send this in WhatsApp blasts, Instagram bio, ads, flyers — every
          submission is attributed to your tenant ({tenant?.name || "—"}).
        </p>

        <div className="mt-6 text-xs uppercase tracking-[0.18em] text-slate-500">
          Branded landing page
        </div>
        <div className="mt-1.5 flex gap-2">
          <Input
            readOnly
            value={landingUrl}
            onClick={(e) => e.target.select()}
            data-testid="share-landing-url"
            className="font-mono text-xs"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => copy(landingUrl, "Landing URL")}
            data-testid="share-copy-landing"
          >
            Copy
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Full scholarship-test funnel. Good for cold traffic; the form URL
          above is better for paid ads / quick contact-only campaigns.
        </p>
      </div>

      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">
          QR for the form URL
        </div>
        <div
          ref={qrRef}
          className="mx-auto inline-block rounded-xl bg-white p-4 ring-1 ring-slate-200"
          data-testid="share-qr"
        >
          <QRCodeCanvas
            value={formUrl}
            size={192}
            level="H"
            includeMargin={false}
          />
        </div>
        <div className="mt-3 flex justify-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={downloadQR}
            data-testid="share-download-qr"
          >
            Download PNG
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          Print on flyers, paste in Instagram stories, or stick on your office
          glass.
        </p>
      </div>
      </div>

      {/* Per-counsellor personal lead links (Tier-1 attribution) */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="counsellor-links-panel">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
              Per-counsellor lead links
            </div>
            <h4 className="font-display mt-1 text-base font-semibold text-[#0A2540]">
              Personal form URLs
            </h4>
          </div>
          <p className="max-w-md text-xs text-slate-500">
            Each link auto-assigns submitted leads to that counsellor. Share via
            WhatsApp, email signature, or Instagram bio for clean attribution.
          </p>
        </div>
        {loadingCouns ? (
          <div className="mt-4 text-sm text-slate-500">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading
            team…
          </div>
        ) : counsellors.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
            No counsellors yet. Add team members in the Counsellors panel to
            generate personal links.
          </div>
        ) : (
          <div className="mt-4 divide-y divide-slate-100">
            {counsellors.map((c) => {
              const url = personalUrl(c.id);
              return (
                <div
                  key={c.id}
                  className="flex flex-wrap items-center gap-3 py-3"
                  data-testid={`counsellor-link-${c.id}`}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0A2540]/10 text-xs font-semibold text-[#0A2540]">
                    {(c.name || c.email || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-[140px] flex-shrink-0">
                    <div className="text-sm font-medium text-[#0A2540]">
                      {c.name || c.email}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                      {c.role}
                    </div>
                  </div>
                  <Input
                    readOnly
                    value={url}
                    onClick={(e) => e.target.select()}
                    className="h-9 flex-1 min-w-[200px] font-mono text-[11px]"
                    data-testid={`counsellor-url-${c.id}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copy(url, `${c.name || "Counsellor"}'s link`)}
                    data-testid={`counsellor-copy-${c.id}`}
                  >
                    Copy
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


function WhisperUsagePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/voice-notes/usage")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 text-sm text-slate-500" data-testid="whisper-usage-loading">
        <Loader2 className="inline h-4 w-4 mr-2 animate-spin" />
        Loading Walk-and-Talk usage…
      </div>
    );
  }
  if (!data) return null;

  const pct = Math.min(100, Math.round(data.pct || 0));
  const bar =
    pct >= 90
      ? "bg-rose-500"
      : pct >= 70
        ? "bg-amber-500"
        : "bg-emerald-500";
  const label =
    pct >= 90
      ? "Near monthly limit"
      : pct >= 70
        ? "Watch usage"
        : "Healthy";

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="whisper-usage-panel">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <Mic className="h-4 w-4 text-violet-600" />
        <div className="font-semibold text-[#0A2540]">
          Walk-and-Talk · Whisper budget
        </div>
        <Badge variant="outline" className="ml-auto text-[10px]">
          {data.month}
        </Badge>
      </div>
      <div className="px-5 py-4">
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-slate-700">
            <span className="font-mono-clock text-xl text-[#0A2540]" data-testid="whisper-used-minutes">
              {data.used_minutes}
            </span>
            <span className="text-slate-400"> / {data.cap_minutes} min</span>
          </span>
          <span
            className={`text-[11px] font-medium uppercase tracking-wider ${
              pct >= 90 ? "text-rose-600" : pct >= 70 ? "text-amber-700" : "text-emerald-700"
            }`}
            data-testid="whisper-usage-label"
          >
            {label}
          </span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full ${bar} transition-all`}
            style={{ width: `${pct}%` }}
            data-testid="whisper-usage-bar"
          />
        </div>
        <p className="mt-3 text-[11px] text-slate-500">
          {data.calls} clip{data.calls === 1 ? "" : "s"} transcribed this month
          · {data.remaining_minutes} min remaining · cap resets on the 1st.
          Per-clip limit: 2 minutes.
        </p>
      </div>
    </div>
  );
}



function ChangePasswordPanel() {
  const [oldPw, setOldPw] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const rules = {
    length: pw.length >= 12,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
  };
  const allPass = rules.length && rules.upper && rules.lower && rules.digit;
  const matches = pw && pw === pw2;
  const canSubmit = !!oldPw && allPass && matches && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldPw,
        new_password: pw,
      });
      toast.success("Password updated. Use the new one next time you log in.");
      setOldPw("");
      setPw("");
      setPw2("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not update password.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="change-password-panel">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-[#0A2540]" />
        <div className="font-semibold text-[#0A2540]">Change password</div>
      </div>
      <form onSubmit={onSubmit} className="px-5 py-5 space-y-4">
        <div>
          <Label htmlFor="cpw-old" className="text-sm">Current password</Label>
          <Input
            id="cpw-old"
            type="password"
            required
            value={oldPw}
            onChange={(e) => setOldPw(e.target.value)}
            placeholder="••••••••"
            className="mt-1.5 h-11 max-w-sm"
            data-testid="cpw-old"
          />
        </div>
        <div>
          <Label htmlFor="cpw-new" className="text-sm">New password</Label>
          <Input
            id="cpw-new"
            type="password"
            required
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="At least 12 characters"
            className="mt-1.5 h-11 max-w-sm"
            data-testid="cpw-new"
          />
        </div>
        <div>
          <Label htmlFor="cpw-confirm" className="text-sm">Confirm new password</Label>
          <Input
            id="cpw-confirm"
            type="password"
            required
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            placeholder="••••••••"
            className="mt-1.5 h-11 max-w-sm"
            data-testid="cpw-confirm"
          />
        </div>

        <ul className="text-xs space-y-1 max-w-sm">
          <RuleRow ok={rules.length} text="At least 12 characters" />
          <RuleRow ok={rules.upper} text="One uppercase letter" />
          <RuleRow ok={rules.lower} text="One lowercase letter" />
          <RuleRow ok={rules.digit} text="One digit" />
          <RuleRow ok={!!matches} text="Passwords match" />
        </ul>

        <Button
          type="submit"
          disabled={!canSubmit}
          className="bg-[#0A2540] hover:bg-[#1E3A5F] disabled:opacity-50 mt-1"
          data-testid="cpw-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating…
            </>
          ) : (
            "Update password"
          )}
        </Button>
      </form>
    </div>
  );
}

function RuleRow({ ok, text }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? "text-emerald-700" : "text-slate-500"}`}>
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-slate-300"}`}
      />
      {text}
    </li>
  );
}
